import { populateDocuments, populateDocumentTitle } from "@/lib/utils";
import { AppAction, AppState } from "./types";

const initialInsightsState: AppState["insights"] = {
  query: "",
  selectedContextValue: "",
  mode: "search",
  phase: "idle",
  result: null,
  error: null,
  lastRequest: null,
  sessionId: null,
};

export const initialState: AppState = {
  agents: null,
  assessments: null,
  error: null,
  classificationFilters: ["accuracy"],
  chatMessages: [],
  commandMenuVisible: false,
  copilotVisible: false,
  courses: null,
  currentDocument: null,
  currentRubric: null,
  documents: null,
  documentsView: "ready",
  expandedBlockId: null,
  grading: null,
  gradeDistribution: null,
  gradingPanel: { visible: false, view: "remarks" },
  firstSuggestionId: null,
  focusMode: false,
  isGeneratingReport: false,
  isNoteEditorOpen: false,
  isPublishingFeedback: false,
  isScratchpadOpen: false,
  isDiscrepanciesPanelOpen: false,
  keyboardShortcutsVisible: false,
  referencePanel: { visible: false, view: "references" },
  selectedBlockId: null,
  selectedBlockIndex: 0,
  selectedGroup: null,
  selectedSuggestion: null,
  selectedSuggestionIndex: null,
  user: null,
  insights: initialInsightsState,
  onboarding: {
    navigate: false,
    highlight: false,
    comment: false,
    suggestion: false,
    generate: false,
    publish: false,
  },
};

export const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case "ADD_AGENT":
      return {
        ...state,
        agents: [...(state.agents || []), action.payload],
      };

    case "ADD_ASSESSMENT":
      return {
        ...state,
        assessments: [...(state.assessments || []), action.payload],
      };

    case "ADD_COURSE":
      return {
        ...state,
        courses: [...(state.courses || []), action.payload],
      };

    case "ADD_DOCUMENT":
      return {
        ...state,
        documents: [...(state.documents || []), populateDocumentTitle(action.payload)],
      };

    case "COMPLETE_ONBOARDING":
      return {
        ...state,
        user: state.user ? { ...state.user, onboardingCompleted: true } : null,
      };

    case "CREATE_SUGGESTION": {
      if (!state.currentDocument) return state;

      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          suggestions: [...state.currentDocument.suggestions, action.payload],
        },
        onboarding: {
          ...state.onboarding,
          comment: true,
        },
      };
    }

    case "DELETE_AGENT": {
      return {
        ...state,
        agents: state.agents!.filter((agent) => agent.id !== action.payload),
      };
    }

    case "DELETE_ASSESSMENT": {
      return {
        ...state,
        assessments: state.assessments!.filter(
          (assessment) => assessment.id !== action.payload
        ),
      };
    }

    case "DELETE_DOCUMENT": {
      return {
        ...state,
        documents: state.documents!.filter((doc) => doc.id !== action.payload),
      };
    }

    case "DELETE_ONBOARDING": {
      return {
        ...state,
        assessments:
          state.assessments?.filter(
            (assessment) => assessment.metaType !== "onboarding"
          ) || null,
        courses:
          state.courses?.filter((course) => course.metaType !== "onboarding") ||
          null,
        documents:
          state.documents?.filter(
            (document) => document.metaType !== "onboarding"
          ) || null,
        user: state.user ? { ...state.user, onboardingCompleted: true } : null,
      };
    }

    case "DELETE_SUGGESTION": {
      if (!state.currentDocument) return state;

      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          suggestions: state.currentDocument.suggestions.filter(
            (s) => s.id !== action.payload
          ),
        },
      };
    }

    case "ENTER_FOCUS_MODE":
      return { ...state, focusMode: true };

    case "EXIT_FOCUS_MODE":
      return { ...state, focusMode: false };

    case "SET_AGENTS":
      return { ...state, agents: action.payload };

    case "SET_ASSESSMENTS":
      return { ...state, assessments: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "CLEAR_ERROR":
      return { ...state, error: null };

    case "SET_COURSES":
      return {
        ...state,
        courses: action.payload,
      };

    case "SET_USER_DATA":
      return {
        ...state,
        courses: action.payload.courses,
        assessments: action.payload.assessments,
        documents: populateDocuments(action.payload.documents),
        user: action.payload.user,
        agents: action.payload.agents,
      };

    case "SET_CURRENT_DOCUMENT": {
      const suggestions = (action.payload?.suggestions ?? []).filter(
        (s) => s.rubricCriteriaId
      );
      const firstSuggestionId =
        suggestions.length > 0 ? suggestions[0].id : null;

      const firstGroup = action.payload?.groups?.[0] || null;
      const firstBlockId = firstGroup?.blockIds?.[0] || null;

      return {
        ...state,
        currentDocument: action.payload ? populateDocumentTitle(action.payload) : null,
        selectedGroup: firstGroup,
        selectedBlockIndex: 0,
        selectedBlockId: firstBlockId,
        selectedSuggestionIndex: null,
        referencePanel: { visible: false, view: "references" },
        gradingPanel: { visible: false, view: "remarks" },
        isDiscrepanciesPanelOpen: false,
        grading: null,
        gradeDistribution: null,
        chatMessages: null,
        firstSuggestionId: firstSuggestionId ?? null,
        isPublishingFeedback: false,
        ...(action.payload === null && {
          selectedBlockIndex: 0,
          selectedBlockId: null,
          expandedBlockId: null,
          currentRubric: null,
          selectedSuggestionIndex: null,
        }),
      };
    }

    case "SET_DOCUMENTS":
      return { ...state, documents: populateDocuments(action.payload) };

    case "SET_DOCUMENTS_VIEW":
      return { ...state, documentsView: action.payload };

    case "SET_EXPANDED_BLOCK_ID":
      return { ...state, expandedBlockId: action.payload };

    case "SET_GENERATING_REPORT":
      return { ...state, isGeneratingReport: action.payload };

    case "SET_GRADING":
      return { ...state, grading: action.payload };

    case "SET_GRADE_DISTRIBUTION":
      return { ...state, gradeDistribution: action.payload };

    case "SET_NOTE_EDITOR_OPEN":
      return { ...state, isNoteEditorOpen: action.payload };

    case "SET_SELECTED_BLOCK_INDEX": {
      const index =
        typeof action.payload === "number"
          ? action.payload
          : action.payload.index;
      const fromKeyboard =
        typeof action.payload === "object"
          ? action.payload.fromKeyboard
          : false;
      const selectedBlockId = state.selectedGroup?.blockIds[index] || null;
      return {
        ...state,
        selectedBlockIndex: index,
        selectedBlockId: selectedBlockId,
        expandedBlockId: null,
        selectedSuggestionIndex: null, // Clear suggestion selection when selecting a block

        // If the user navigates using keyboard, mark the navigation onboarding step as completed
        ...(fromKeyboard &&
          state.onboarding && {
            onboarding: {
              ...state.onboarding,
              navigate: true,
            },
          }),
      };
    }

    case "SET_SELECTED_GROUP":
      return {
        ...state,
        selectedGroup: action.payload,
        selectedBlockIndex: 0,
        selectedBlockId: action.payload?.blockIds?.[0] || null,
        selectedSuggestionIndex: null,
      };

    case "SET_SELECTED_SUGGESTION":
      return { ...state, selectedSuggestion: action.payload };

    case "SET_SELECTED_SUGGESTION_INDEX":
      return {
        ...state,
        selectedSuggestionIndex: action.payload,
        // Clear block selection when selecting a suggestion
        ...(action.payload !== null && {
          selectedBlockId: null,
          selectedBlockIndex: -1, // Reset to invalid index to clear highlighting
        }),
      };

    case "CLEAR_SUGGESTION_SELECT_BLOCK": {
      const selectedBlockId =
        state.selectedGroup?.blockIds[action.payload] || null;
      return {
        ...state,
        selectedSuggestionIndex: null,
        selectedBlockIndex: action.payload,
        selectedBlockId: selectedBlockId,
        expandedBlockId: null,
      };
    }

    case "TOGGLE_COMMAND_MENU":
      return { ...state, commandMenuVisible: !state.commandMenuVisible };

    case "TOGGLE_COPILOT":
      return { ...state, copilotVisible: !state.copilotVisible };

    case "TOGGLE_KEYBOARD_SHORTCUTS":
      return {
        ...state,
        keyboardShortcutsVisible: !state.keyboardShortcutsVisible,
      };

    case "SHOW_REFERENCE_PANEL":
      return {
        ...state,
        referencePanel: {
          visible: action.payload.visible,
          view: action.payload.view || state.referencePanel.view,
        },
      };

    case "SHOW_GRADING_PANEL":
      return {
        ...state,
        gradingPanel: {
          visible: action.payload.visible,
          view: action.payload.view || state.gradingPanel.view,
          selectedCriterionId: action.payload.selectedCriterionId,
        },
      };

    case "TOGGLE_SCRATCHPAD":
      return { ...state, isScratchpadOpen: !state.isScratchpadOpen };

    case "TOGGLE_DISCREPANCIES_PANEL":
      return {
        ...state,
        isDiscrepanciesPanelOpen: !state.isDiscrepanciesPanelOpen,
      };

    case "UPDATE_AGENT":
      return {
        ...state,
        agents: (state.agents || []).map((agent) =>
          agent.id === action.payload.id
            ? { ...agent, ...action.payload }
            : agent
        ),
      };

    case "UPDATE_ASSESSMENT":
      return {
        ...state,
        assessments: (state.assessments || []).map((assessment) =>
          assessment.id === action.payload.id
            ? { ...assessment, ...action.payload }
            : assessment
        ),
      };

    case "UPDATE_COURSE":
      return {
        ...state,
        courses: state.courses!.map((course) =>
          course.id === action.payload.id ? { ...course, ...action.payload } : course
        ),
      };

    case "UPDATE_BLOCK_SENTIMENT": {
      if (!state.currentDocument) return state;

      const { blockId, sentiment } = action.payload;
      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          blocks: state.currentDocument.blocks.map((block) => {
            if (block.id !== blockId) return block;
            return { ...block, sentiment };
          }),
        },
        onboarding: {
          ...state.onboarding,
          highlight: true,
        },
      };
    }

    case "UPDATE_DOCUMENT_NOTES": {
      const { id, notes } = action.payload;
      return {
        ...state,
        documents: state.documents!.map((doc) =>
          doc.id === id ? { ...doc, notes } : doc
        ),
        currentDocument:
          state.currentDocument?.id === id
            ? { ...state.currentDocument, notes }
            : state.currentDocument,
      };
    }

    case "UPDATE_FEEDBACK_SUMMARY": {
      return {
        ...state,
        grading: state.grading
          ? {
              ...state.grading,
              feedback: {
                ...state.grading.feedback,
                summary: action.payload,
              },
            }
          : null,
      };
    }
    case "UPDATE_DOCUMENT_STATUS":
      return {
        ...state,
        currentDocument: state.currentDocument
          ? {
              ...state.currentDocument,
              status: action.payload.status,
            }
          : null,
        documents:
          state.documents?.map((doc) =>
            doc.id === action.payload.id
              ? { ...doc, status: action.payload.status }
              : doc
          ) || null,
      };

    case "UPDATE_DOCUMENT_PROGRESS":
      return {
        ...state,
        documents:
          state.documents?.map((doc) =>
            doc.id === action.payload.id
              ? { ...doc, progress: action.payload.progress }
              : doc
          ) || null,
      };

    case "UPDATE_SUGGESTION": {
      if (!state.currentDocument) return state;

      const existingSuggestion = state.currentDocument.suggestions.find(
        (s) => s.id === action.payload.id
      );

      const completeOnboarding =
        existingSuggestion?.status === "pending" &&
        (action.payload.status === "approved" ||
          action.payload.status === "rejected");

      return {
        ...state,
        currentDocument: {
          ...state.currentDocument,
          suggestions: state.currentDocument.suggestions.map((s) => {
            if (s.id === action.payload.id) return action.payload;
            return s;
          }),
        },
        onboarding: {
          ...state.onboarding,
          suggestion: state.onboarding.suggestion || completeOnboarding,
        },
      };
    }

    case "SET_CLASSIFICATION_FILTERS":
      return { ...state, classificationFilters: action.payload };

    case "UPDATE_FINAL_GRADE":
      return {
        ...state,
        grading: state.grading
          ? { ...state.grading, grade: action.payload }
          : null,
        gradeDistribution: null,
        documents:
          state.documents?.map((doc) =>
            state.currentDocument && doc.id === state.currentDocument.id
              ? { ...doc, grade: action.payload }
              : doc
          ) || null,
      };

    case "SET_IS_PUBLISHING_FEEDBACK":
      return {
        ...state,
        isPublishingFeedback: action.payload,
      };

    case "UPDATE_FEEDBACK": {
      return {
        ...state,
        isPublishingFeedback: false,
        grading:
          state.grading && state.grading.id === action.payload.id
            ? {
                ...state.grading,
                feedback: {
                  ...state.grading.feedback,
                  ...action.payload.feedback,
                  status: action.payload.feedback.status,
                },
              }
            : state.grading,
        onboarding: {
          ...state.onboarding,
          publish:
            state.grading?.feedback?.status === "published" ||
            action.payload.feedback.status === "published",
        },
      };
    }
    case "SAVE_FEEDBACK": {
      return {
        ...state,
        grading:
          state.grading && state.grading.id === action.payload.id
            ? {
                ...state.grading,
                feedback: {
                  ...state.grading.feedback,
                  summary: action.payload.feedback.summary,
                  status: action.payload.feedback.status,
                },
              }
            : state.grading,
      };
    }
    case "SET_CHAT_MESSAGES": {
      return {
        ...state,
        chatMessages: action.payload,
      };
    }

    case "SET_CURRENT_RUBRIC": {
      return {
        ...state,
        currentRubric: action.payload,
      };
    }

    case "ADD_CHAT_MESSAGE": {
      return {
        ...state,
        chatMessages: [...(state.chatMessages || []), action.payload],
      };
    }

    case "UPDATE_CHAT_MESSAGE": {
      const messages = state.chatMessages || [];
      const lastIndex = messages.length - 1;

      if (lastIndex >= 0 && messages[lastIndex].role === "assistant") {
        // Update the last assistant message
        const updatedMessages = [...messages];
        updatedMessages[lastIndex] = {
          ...updatedMessages[lastIndex],
          message: action.payload.message,
        };
        return {
          ...state,
          chatMessages: updatedMessages,
        };
      } else {
        // Add a new assistant message
        return {
          ...state,
          chatMessages: [...messages, action.payload],
        };
      }
    }

    case "CLEAR_CHAT_MESSAGES": {
      return {
        ...state,
        chatMessages: [],
      };
    }

    case "SET_ONBOARDING": {
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          generate: action.payload.generate,
        },
      };
    }

    case "UPDATE_USER_NOTES": {
      return {
        ...state,
        user: state.user ? { ...state.user, notes: action.payload } : null,
      };
    }

    case "UPDATE_USER_SETTINGS": {
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              settings: { ...state.user.settings, ...action.payload },
            }
          : null,
      };
    }

    case "SET_INSIGHTS_STATE": {
      return {
        ...state,
        insights: {
          ...state.insights,
          ...action.payload,
        },
      };
    }

    case "RESET_INSIGHTS_STATE": {
      return {
        ...state,
        insights: initialInsightsState,
      };
    }

    default:
      return state;
  }
};
